import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Sun, LogOut, Users, TrendingUp, DollarSign, Calendar, 
  Phone, Mail, MapPin, Trash2, RefreshCw, Search,
  ChevronLeft, ChevronRight
} from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [totalLeads, setTotalLeads] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const leadsPerPage = 10;

  const token = localStorage.getItem("adminToken");
  const username = localStorage.getItem("adminUsername");

  useEffect(() => {
    if (!token) {
      navigate("/admin");
      return;
    }
    verifyAndFetch();
  }, [token, navigate]);

  const verifyAndFetch = async () => {
    try {
      const verifyResponse = await axios.get(`${API}/admin/verify?token=${token}`);
      if (!verifyResponse.data.valid) {
        handleLogout();
        return;
      }
      await Promise.all([fetchStats(), fetchLeads()]);
    } catch (error) {
      console.error("Verification error:", error);
      handleLogout();
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/stats?token=${token}`);
      setStats(response.data);
    } catch (error) {
      console.error("Stats fetch error:", error);
    }
  };

  const fetchLeads = async (page = 1) => {
    setIsLoading(true);
    try {
      const skip = (page - 1) * leadsPerPage;
      const response = await axios.get(`${API}/admin/leads?token=${token}&limit=${leadsPerPage}&skip=${skip}`);
      setLeads(response.data.leads);
      setTotalLeads(response.data.total);
      setCurrentPage(page);
    } catch (error) {
      console.error("Leads fetch error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteLead = async (leadId) => {
    if (!window.confirm("Are you sure you want to delete this lead?")) return;
    
    try {
      await axios.delete(`${API}/admin/leads/${leadId}?token=${token}`);
      toast.success("Lead deleted successfully");
      fetchLeads(currentPage);
      fetchStats();
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Failed to delete lead");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    localStorage.removeItem("adminUsername");
    navigate("/admin");
  };

  const handleRefresh = () => {
    fetchStats();
    fetchLeads(currentPage);
    toast.success("Data refreshed");
  };

  const filteredLeads = leads.filter(lead => 
    lead.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    lead.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    lead.phone?.includes(searchTerm)
  );

  const totalPages = Math.ceil(totalLeads / leadsPerPage);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-cream-100" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-cream-200 sticky top-0 z-50">
        <div className="max-w-[1600px] mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-solar-orange rounded-full flex items-center justify-center">
              <Sun className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="font-heading text-xl text-gray-900">Admin Dashboard</h1>
              <p className="text-gray-500 text-sm">Welcome, {username}</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Button
              onClick={handleRefresh}
              variant="outline"
              className="border-cream-300 text-gray-600 hover:text-solar-orange rounded-lg"
              data-testid="refresh-btn"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="border-red-200 text-red-600 hover:bg-red-50 rounded-lg"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto px-6 py-8">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-xl p-6 shadow-sm border border-cream-200">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-solar-orange/10 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-solar-orange" />
                </div>
                <span className="text-gray-500 text-sm">Total Leads</span>
              </div>
              <p className="font-heading text-4xl text-gray-900">{stats.total_leads}</p>
            </div>
            
            <div className="bg-white rounded-xl p-6 shadow-sm border border-cream-200">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-green-600" />
                </div>
                <span className="text-gray-500 text-sm">Today</span>
              </div>
              <p className="font-heading text-4xl text-gray-900">{stats.leads_today}</p>
            </div>
            
            <div className="bg-white rounded-xl p-6 shadow-sm border border-cream-200">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-blue-600" />
                </div>
                <span className="text-gray-500 text-sm">This Month</span>
              </div>
              <p className="font-heading text-4xl text-gray-900">{stats.leads_this_month}</p>
            </div>
            
            <div className="bg-white rounded-xl p-6 shadow-sm border border-cream-200">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-purple-600" />
                </div>
                <span className="text-gray-500 text-sm">Avg Bill</span>
              </div>
              <p className="font-heading text-4xl text-gray-900">${stats.avg_monthly_bill}</p>
            </div>
          </div>
        )}

        {/* Top Utilities */}
        {stats?.top_utilities?.length > 0 && (
          <div className="bg-white rounded-xl p-6 shadow-sm border border-cream-200 mb-8">
            <h2 className="font-heading text-lg text-gray-900 mb-4">Top Utilities</h2>
            <div className="flex flex-wrap gap-3">
              {stats.top_utilities.map((utility, index) => (
                <div key={index} className="bg-cream-100 rounded-lg px-4 py-2 flex items-center gap-2">
                  <span className="text-gray-700">{utility.name || "Unknown"}</span>
                  <span className="bg-solar-orange text-white text-xs px-2 py-1 rounded-full">
                    {utility.count}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Leads Table */}
        <div className="bg-white rounded-xl shadow-sm border border-cream-200 overflow-hidden">
          <div className="p-6 border-b border-cream-200 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <h2 className="font-heading text-xl text-gray-900">All Leads</h2>
            <div className="relative w-full md:w-auto">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search leads..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full md:w-64 input-light h-10 pl-10 pr-4 rounded-lg text-sm"
                data-testid="search-input"
              />
            </div>
          </div>

          {isLoading ? (
            <div className="p-12 text-center">
              <RefreshCw className="w-8 h-8 text-solar-orange animate-spin mx-auto mb-4" />
              <p className="text-gray-500">Loading leads...</p>
            </div>
          ) : filteredLeads.length === 0 ? (
            <div className="p-12 text-center">
              <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No leads found</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-cream-50">
                    <tr>
                      <th className="text-left px-6 py-4 text-gray-600 font-medium text-sm">Contact</th>
                      <th className="text-left px-6 py-4 text-gray-600 font-medium text-sm">Details</th>
                      <th className="text-left px-6 py-4 text-gray-600 font-medium text-sm">Utility</th>
                      <th className="text-left px-6 py-4 text-gray-600 font-medium text-sm">Bill</th>
                      <th className="text-left px-6 py-4 text-gray-600 font-medium text-sm">Type</th>
                      <th className="text-left px-6 py-4 text-gray-600 font-medium text-sm">Date</th>
                      <th className="text-left px-6 py-4 text-gray-600 font-medium text-sm">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-cream-200">
                    {filteredLeads.map((lead) => (
                      <tr key={lead.id} className="hover:bg-cream-50 transition-colors" data-testid={`lead-row-${lead.id}`}>
                        <td className="px-6 py-4">
                          <div>
                            <p className="font-medium text-gray-900">{lead.full_name}</p>
                            <div className="flex items-center gap-1 text-gray-500 text-sm mt-1">
                              <Mail className="w-3 h-3" />
                              <span>{lead.email}</span>
                            </div>
                            <div className="flex items-center gap-1 text-gray-500 text-sm">
                              <Phone className="w-3 h-3" />
                              <span>{lead.phone}</span>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          {lead.address && (
                            <div className="flex items-center gap-1 text-gray-600 text-sm">
                              <MapPin className="w-3 h-3" />
                              <span className="truncate max-w-[200px]">{lead.address}</span>
                            </div>
                          )}
                          {lead.home_ownership && (
                            <span className="text-xs text-gray-500 capitalize">{lead.home_ownership}</span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-sm text-gray-700">{lead.utility_company || "-"}</span>
                        </td>
                        <td className="px-6 py-4">
                          {lead.monthly_bill ? (
                            <span className="font-medium text-gray-900">${lead.monthly_bill}</span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <span className={`text-xs px-2 py-1 rounded-full capitalize ${
                            lead.lead_type === 'calculator' 
                              ? 'bg-blue-100 text-blue-700' 
                              : 'bg-green-100 text-green-700'
                          }`}>
                            {lead.lead_type}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {formatDate(lead.created_at)}
                        </td>
                        <td className="px-6 py-4">
                          <button
                            onClick={() => handleDeleteLead(lead.id)}
                            className="text-red-500 hover:text-red-700 p-2 hover:bg-red-50 rounded-lg transition-colors"
                            data-testid={`delete-lead-${lead.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="p-6 border-t border-cream-200 flex items-center justify-between">
                <p className="text-gray-500 text-sm">
                  Showing {((currentPage - 1) * leadsPerPage) + 1} to {Math.min(currentPage * leadsPerPage, totalLeads)} of {totalLeads} leads
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    onClick={() => fetchLeads(currentPage - 1)}
                    disabled={currentPage === 1}
                    variant="outline"
                    className="border-cream-300 disabled:opacity-50"
                    data-testid="prev-page-btn"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-gray-600 px-4">
                    Page {currentPage} of {totalPages}
                  </span>
                  <Button
                    onClick={() => fetchLeads(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    variant="outline"
                    className="border-cream-300 disabled:opacity-50"
                    data-testid="next-page-btn"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
